import { Service } from 'typedi';
import { BaseAddEditViewModel } from '../../../../core/services/BaseViewModel';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { DocumentsModel, SecureFileUploadRequestModel } from './SecureFileUploadModel';
import { ListItem } from '../../../../core/services/BaseService';
import { Appl } from '../../../../core/Appl';
import { ILovService } from '../../../utility/lov/LovService';
import { ISecureFileUploadService } from '../SecureFileUploadService';

@Service() // eslint-disable-next-line
export default class SecureFileUploadViewModel extends BaseAddEditViewModel {
    private service: ISecureFileUploadService;
    private lovService: ILovService;

    @observable public documentUploadLinkValid = true;
    @observable public documentUploadLinkInValidMessage = '';
    @observable public documentUploadSuccess = false;
    @observable public uploadDialogVisible = false;
    @observable public requestModel: SecureFileUploadRequestModel;
    @observable public documentListModel: Array<ListItem> = [];
    @observable public activeDocument: DocumentsModel;
    @observable public selectedType: string = '';
    @observable public additionalDocUpload = false;
    @observable public reqId?: number;
    @observable public secret?: string;

    constructor() {
        super('Secure File Upload');
        this.service = Appl.Services.get<ISecureFileUploadService>('ISecureFileUploadService');
        this.lovService = Appl.Services.get<ILovService>('ILovService');
        this.requestModel = { documents: [] };
        this.activeDocument = {}
        makeObservable(this);
    }

    @action public async initAsync(reqId: number, secret: string): Promise<void> {
        this.reqId = reqId;
        this.secret = secret;
        this.documentUploadSuccess = false;
        const result = await this.service?.getUploadRequestByReqIdAndSecretAsync(reqId, secret);
        if (result.success) {
            runInAction(() => {
                this.documentUploadLinkValid = true;
                this.requestModel = result.data;
                this.requestModel.reqKey = secret;
                if (this.requestModel.documents && this.requestModel.documents.length > 0) {
                    this.documentUploadSuccess = true;
                }
            })
            const docListResponse = await this.lovService?.getListOfValuesAsync('scers_member.lov_doc_type');
            if (docListResponse.success) {
                runInAction(() => {
                    //this.documentListModel.push({ key: "", value: "Select Document Type." })
                    this.documentListModel.push(...docListResponse.data);
                })
            } else {
                Appl.Error.handle(docListResponse.error);
            }
        } else {
            this.documentUploadLinkValid = false;
            this.documentUploadLinkInValidMessage = result.error?.detail!;

        }
    }

    @action public async showUploadDialogAsync(additionalDocs: boolean): Promise<void> {
        this.uploadDialogVisible = true;
        this.activeDocument = {}
        this.additionalDocUpload = additionalDocs;
    }

    @action public async hideUploadDialogAsync(): Promise<void> {
        this.uploadDialogVisible = false;
        this.activeDocument = {}
    }

    @action public async onAddDocumentAsync(): Promise<void> {
        Appl.Validator.init();
        Appl.Validator.validateString("Document Type", "docTypeId", this.activeDocument.docTypeId, true);
        Appl.Validator.validateString("Document Name", "docName", this.activeDocument.docName, true);
        if (!Appl.Validator.isValid()) {
            return;
        }
        if (!this.requestModel?.documents) {
            this.requestModel.documents = [];
        }
        if (!this.activeDocument?.file) {
            Appl.MessageBox.show(`Please select a document.`)
            return;
        }
        let existingDoc = this.requestModel.documents?.find(o => o.docFilename === this.activeDocument?.file.name);
        if (existingDoc) {
            Appl.MessageBox.show(`The file with name '${this.activeDocument?.file.name}' has already been uploaded. Choose another document to continue.`, 'Duplicate File Name Detected.');
            return;
        }
        if (this.additionalDocUpload) {
            this.requestModel.documents = [];
            this.additionalDocUpload = false;
            this.documentUploadSuccess = false;
        }
        this.activeDocument.docType = this.documentListModel.find(o => o.key === this.activeDocument.docTypeId)?.value;
        this.requestModel.documents.push(this.activeDocument);
        await this.hideUploadDialogAsync();
    }

    @action public async onRemoveDocumentAsync(document: DocumentsModel): Promise<void> {
        if (document.docDtlId) {
            const result = await this.service?.deleteMemberFileAsync(document.docDtlId);
            if (result.success) {
                this.requestModel.documents = this.requestModel.documents?.filter(item => item !== document);
            } else {
                Appl.Error.handle(result.error);
            }
        } else {
            this.requestModel.documents = this.requestModel.documents?.filter(item => item !== document);
        }
        if (this.requestModel.documents && this.requestModel.documents?.length == 0) {
            const uploadResult = await this.service?.getUploadRequestByReqIdAndSecretAsync(this.reqId!, this.secret!);
            if (uploadResult.success) {
                runInAction(() => {
                    this.requestModel = uploadResult.data;
                    this.requestModel.reqKey = this.secret;
                    if (this.requestModel.documents && this.requestModel.documents.length > 0) {
                        this.documentUploadSuccess = true;
                    }
                })
            } else {
                Appl.Error.handle(uploadResult.error, true);
            }
        }
    }

    @action public async onSubmitAsync(): Promise<void> {
        this.documentUploadSuccess = false;
        if (this.requestModel.documents?.length === 0) {
            this.showError('Please Add Documents');
            return;
        }
        const result = await this.service?.uploadAsync('/members/secure-file-upload/submit-member-files', this.requestModel);
        if (result.success) {
            this.documentUploadSuccess = true;
            const uploadResult = await this.service?.getUploadRequestByReqIdAndSecretAsync(this.reqId!, this.secret!);
            if (uploadResult.success) {
                runInAction(() => {
                    this.requestModel = uploadResult.data;
                    this.requestModel.reqKey = this.secret;
                })
            } else {
                Appl.Error.handle(uploadResult.error, true);
            }

        } else {
            Appl.Error.handle(result.error, true);
        }
    }
};

